import { useTranslation } from 'react-i18next'

import { CustomerSmsBadge } from '#/entities/number-health'
import { cn } from '#/shared/lib/utils'

import { highlightTerms } from '../lib/highlight-terms'
import type { MessageHitRow } from '../lib/map-message-hit'
import { ActivityAvatar } from './activity-avatar'

/** The snippet with every searched word emphasised — the server returns the
 *  excerpt unmarked, so the match is located client-side. */
function Snippet({ text, query }: { text: string; query: string }) {
  return (
    <>
      {highlightTerms(text, query).map((segment, index) =>
        segment.match ? (
          <mark
            key={index}
            className="bg-transparent font-semibold text-gray-900"
          >
            {segment.text}
          </mark>
        ) : (
          <span key={index}>{segment.text}</span>
        ),
      )}
    </>
  )
}

interface MessageHitItemProps {
  hit: MessageHitRow
  /** The term being searched, for client-side highlighting. */
  query: string
  /** Hide the counterparty title — the in-chat search is already scoped to it. */
  hideTitle?: boolean
  onOpen: (hit: MessageHitRow) => void
}

/**
 * One message/transcript search hit. Opening it jumps to that message inside its
 * thread (the thread loads the page *around* it), rather than to the thread tail.
 */
export function MessageHitItem({
  hit,
  query,
  hideTitle = false,
  onOpen,
}: MessageHitItemProps) {
  const { t } = useTranslation()
  return (
    <button
      type="button"
      onClick={() => onOpen(hit)}
      className="flex w-full items-center gap-2.5 px-4 py-2.5 text-left transition-colors hover:bg-paper-avatar"
    >
      {!hideTitle && <ActivityAvatar title={hit.title} icon={hit.icon} />}
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <div className="flex min-w-0 items-center gap-1.5">
          {!hideTitle && (
            <>
              <span className="min-w-0 truncate text-sm font-medium text-gray-900">
                {hit.title}
              </span>
              <CustomerSmsBadge customer={hit.deliverability} />
            </>
          )}
          <span
            className={cn(
              'shrink-0 text-xs whitespace-nowrap text-warm-500',
              hideTitle ? '' : 'ml-auto pl-1',
            )}
          >
            {hit.timestamp}
          </span>
        </div>
        <span className="line-clamp-2 text-xs text-warm-500">
          {hit.outbound && (
            <span className="text-gray-500">
              {t('phonePanel.search.youPrefix')}
            </span>
          )}
          <Snippet text={hit.snippet} query={query} />
        </span>
      </div>
    </button>
  )
}
